import {
  LoadingSpinner,
  SearchInput,
  Alert,
  Link,
  Tooltip,
  Toast,
} from '@paypalcorp/pp-react'
import React, {
  useState,
  useEffect,
  useContext,
  useReducer,
  Fragment,
} from 'react'
import GrantAddPerm from './pages/GrantAddPerm'
import ViewPerm from './pages/viewPerm'
import EditPerm from './pages/editPerm'
import DeleteEntry from './pages/deleteEntry'
import HelpSheet from './pages/helpSheet'
import { MessageFormat } from '@paypalcorp/worldready'
import {
  Message,
  Context as WorldReadyContext,
} from '@paypalcorp/worldready-react'

import apiClient from '../api'
import errorHandler from './pages/utils/errorHandler'
import Header from './pages/utils/Header'
import { consentURL, mobileScreenSize } from '../utils/constants'
import CustomTable from './pages/utils/CustomTable'

// the permissions referenced below are named differently in db than what is returned.
const permBuilder = (newData, permissionsList) => {
  for (let i = 0; i < newData.length; i++) {
    let perms = [...newData[i].grantedPermissions]
    perms = perms.map(function (item) {
      return item === 'EXCEPTION_PROCESSING'
        ? 'EXCEPTION_PROCESSING_REPORT'
        : item
    })
    perms = perms.filter((p) => {
      return permissionsList.filter((pl) => pl.name === p).length >= 1
    })
    newData[i].grantedPermissions = [...perms]
  }
  return newData
}

const sheetReducer = (sheetsState, action) => {
  switch (action.type) {
    case 'add':
      return { ...sheetsState, isAddOpen: action.value }
    case 'grant':
      return { ...sheetsState, isGrantOpen: action.value }
    case 'edit':
      return { ...sheetsState, isEditOpen: action.value }
    case 'delete':
      return { ...sheetsState, isDeleteOpen: action.value }
    case 'view':
      return { ...sheetsState, isViewOpen: action.value }
    case 'help':
      return { ...sheetsState, isHelpOpen: action.value }
    default:
      throw Error('Unknown action: ' + action.type)
  }
}

// method to implement search functionality
const searchEntry = (event, setSearchText, APIData, setSearchRes) => {
  const st = event.target.value
  setSearchText(st)
  if (st !== '') {
    const searchEntries = APIData.filter(
      (e) =>
        e.apiUsername.toLowerCase().includes(st.toLowerCase()) ||
        e.Organization.toLowerCase().includes(st.toLowerCase()),
    )
    setSearchRes([...searchEntries])
  } else {
    setSearchRes([...APIData])
  }
}

const closeSuccessSheet = ({
  message,
  type,
  entry,
  APIData,
  sheetsStateSetter,
  setSuccessMsg,
  setAPIData,
  setSearchText,
  setSearchRes,
}) => {
  sheetsStateSetter({ type, action: false })
  setSuccessMsg(message)
  switch (type) {
    case 'delete':
      const newEntries = APIData.filter(
        (e) => e.accountNumber !== entry.accountNumber,
      )
      setAPIData([...newEntries])
      setSearchText('')
      setSearchRes([...newEntries])
      break
    case 'grant':
      setAPIData([entry, ...APIData])
      setSearchText('')
      setSearchRes([entry, ...APIData])
      break
    case 'edit':
      const nonEditedEntries = APIData.filter(
        (e) => e.accountNumber !== entry.accountNumber,
      )
      setAPIData([entry, ...nonEditedEntries])
      setSearchText('')
      setSearchRes([entry, ...nonEditedEntries])
      break
    default:
      break
  }
}

const handleGrantPerm = ({
  sheetsStateSetter,
  APIData,
  sheetsState,
  permissions,
  errorHandlerFn,
  closeSuccessSheetCallback,
  mobileScreenSizeValue,
  windowWidth,
}) => {
  return (
    <GrantAddPerm
      setGrantOpen={sheetsStateSetter}
      entries={APIData}
      isGrantOpen={sheetsState.isGrantOpen}
      permissions={permissions}
      errorHandler={errorHandlerFn}
      closeSuccessSheet={closeSuccessSheetCallback}
      mobileScreenSize={mobileScreenSizeValue}
      windowWidth={windowWidth}
    />
  )
}

const handleEditPerm = ({
  toChange,
  sheetsStateSetter,
  sheetsState,
  setToChange,
  permissions,
  errorHandlerFn,
  closeSuccessSheetCallback,
  mobileScreenSizeValue,
  windowWidth,
}) => {
  return (
    <div>
      <EditPerm
        party={toChange}
        setEditOpen={sheetsStateSetter}
        isEditOpen={sheetsState.isEditOpen}
        setToChange={setToChange}
        permissions={permissions}
        errorHandler={errorHandlerFn}
        closeSuccessSheet={closeSuccessSheetCallback}
        mobileScreenSize={mobileScreenSizeValue}
        windowWidth={windowWidth}
      />
    </div>
  )
}

const handleViewPerm = ({
  permissions,
  sheetsState,
  toChange,
  sheetsStateSetter,
  setToChange,
  mobileScreenSizeValue,
  windowWidth,
}) => {
  return (
    <ViewPerm
      permissions={permissions}
      isViewOpen={sheetsState.isViewOpen}
      party={toChange}
      setViewOpen={sheetsStateSetter}
      setToChange={setToChange}
      mobileScreenSize={mobileScreenSizeValue}
      windowWidth={windowWidth}
    />
  )
}

const handleDelete = ({
  toChange,
  sheetsState,
  setToChange,
  sheetsStateSetter,
  errorHandlerFn,
  closeSuccessSheetCallback,
}) => {
  return (
    <DeleteEntry
      toChange={toChange}
      isDeleteOpen={sheetsState.isDeleteOpen}
      setToChange={setToChange}
      setDeleteOpen={sheetsStateSetter}
      errorHandler={errorHandlerFn}
      closeSuccessSheet={closeSuccessSheetCallback}
    />
  )
}

const handleHelp = ({
  permissions,
  sheetsState,
  userDetails,
  sheetsStateSetter,
  mobileScreenSizeValue,
  windowWidth,
  setSuccessMsg,
}) => {
  return (
    <HelpSheet
      permissions={permissions}
      isHelpOpen={sheetsState.isHelpOpen}
      userDetails={userDetails}
      setHelpOpen={sheetsStateSetter}
      mobileScreenSize={mobileScreenSizeValue}
      windowWidth={windowWidth}
      setSuccessMsg={setSuccessMsg}
    />
  )
}

const ThirdPartyPerms = () => {
  const worldReady = useContext(WorldReadyContext)
  const [APIData, setAPIData] = useState([]) // Populate this with List of already granted parties, will be set after add, edit operations
  const [searchRes, setSearchRes] = useState([]) // List of entries that contain search text, will have all entries if search text is empty
  const [toChange, setToChange] = useState({}) // Entry in focus to view,edit or delete
  const [sheetsState, sheetsStateSetter] = useReducer(sheetReducer, {
    isGrantOpen: false,
    isEditOpen: false,
    isAddOpen: false,
    isHelpOpen: false,
    isViewOpen: false,
    isDeleteOpen: false,
  })
  const [searchText, setSearchText] = useState('') // for search text
  const [loading, setLoading] = useState(true)
  const [userDetails, setUserDetails] = useState({})
  const [permissions, setPermissions] = useState({})
  const [successMsg, setSuccessMsg] = useState('')
  const [error, setError] = useState('')
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  useEffect(() => {
    const handleWindowResize = () => {
      setWindowWidth(window.innerWidth)
    }
    window.addEventListener('resize', handleWindowResize)
    return () => {
      window.removeEventListener('resize', handleWindowResize)
    }
  })
  // initial fetch of all entries
  useEffect(() => {
    document.title = 'Third Party API Users Management'
    apiClient
      .get('/fetchAllEntries')
      .then((response) => {
        setLoading(false)
        const _data = permBuilder(
          response.data.entries,
          response.data.permissions,
        )
        setSearchRes(_data)
        setAPIData(_data)
        setUserDetails({ ...response.data.userDetails })
        setError('')
        setPermissions([...response.data.permissions])
        return null
      })
      .catch((e) => {
        errorHandler(e, setError, worldReady)
      })
  }, [worldReady])
  if (error !== '') {
    return (
      <div className="tpp_alertIndex_parent">
        <Alert type="error" className="tpp_alertIndex">
          {error}
        </Alert>
      </div>
    )
  }
  if (loading) {
    return (
      <div className="tpp_rootLoading">
        <LoadingSpinner size="xl" />
      </div>
    )
  }
  // Create callback for closeSuccessSheet with bound parameters
  const closeSuccessSheetCallback = (message, type, entry) =>
    closeSuccessSheet({
      message,
      type,
      entry,
      APIData,
      sheetsStateSetter,
      setSuccessMsg,
      setAPIData,
      setSearchText,
      setSearchRes,
    })
  if (sheetsState.isHelpOpen) {
    return (
      <div>
        {handleHelp({
          permissions,
          sheetsState,
          userDetails,
          sheetsStateSetter,
          mobileScreenSizeValue: mobileScreenSize,
          windowWidth,
          setSuccessMsg,
        })}
      </div>
    )
  }
  if (!loading && error === '' && !sheetsState.isHelpOpen) {
    return (
      <Fragment>
        <div className={windowWidth <= mobileScreenSize ? 'tpp_root' : ''}>
          {successMsg !== '' && (
            <div>
              <Toast
                message={successMsg}
                isSuccess={true}
                onClose={() => setSuccessMsg('')}
                id="successAlert"
              />
            </div>
          )}
          {sheetsState.isGrantOpen &&
            handleGrantPerm({
              sheetsStateSetter,
              APIData,
              sheetsState,
              permissions,
              errorHandlerFn: errorHandler,
              closeSuccessSheetCallback,
              mobileScreenSizeValue: mobileScreenSize,
              windowWidth,
            })}
          {sheetsState.isViewOpen &&
            handleViewPerm({
              permissions,
              sheetsState,
              toChange,
              sheetsStateSetter,
              setToChange,
              mobileScreenSizeValue: mobileScreenSize,
              windowWidth,
            })}
          {sheetsState.isEditOpen &&
            handleEditPerm({
              toChange,
              sheetsStateSetter,
              sheetsState,
              setToChange,
              permissions,
              errorHandlerFn: errorHandler,
              closeSuccessSheetCallback,
              mobileScreenSizeValue: mobileScreenSize,
              windowWidth,
            })}
          {sheetsState.isDeleteOpen &&
            handleDelete({
              toChange,
              sheetsState,
              setToChange,
              sheetsStateSetter,
              errorHandlerFn: errorHandler,
              closeSuccessSheetCallback,
            })}
          <Header
            sheetsStateSetter={sheetsStateSetter}
            windowWidth={windowWidth}
          />
          <div>
            <SearchInput
              value={searchText}
              isLabelHidden
              placeholder={new MessageFormat(worldReady, {
                id: `thirdPartyPerm.placeholderSearchText`,
              }).format()}
              onClear={(e) =>
                searchEntry(e, setSearchText, APIData, setSearchRes)
              }
              onChange={(e) =>
                searchEntry(e, setSearchText, APIData, setSearchRes)
              }
            />
          </div>
          <div className="tpp_consent">
            <div>
              <Link href={consentURL}>
                <Message id="thirdPartyPerm.consentLink" />
              </Link>
            </div>
            <Tooltip
              description={new MessageFormat(worldReady, {
                id: `thirdPartyPerm.consentHelpText`,
              }).format()}
            />
          </div>
          <div>
            <CustomTable
              windowWidth={windowWidth}
              APIData={APIData}
              searchRes={searchRes}
              sheetsStateSetter={sheetsStateSetter}
              worldReady={worldReady}
              setToChange={setToChange}
              setSearchRes={setSearchRes}
            />
          </div>
        </div>
      </Fragment>
    )
  }
  return null
}

export default ThirdPartyPerms
